import React, { useState, useEffect } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import TabNavigation from "../../../../../components/common/tabNavigation";
import LoadingOverlay from "../../../../../components/common/LoadingOverlay";
import {
  getMissionHistoryAPI,
  getKeywordNameAPI,
  getCoupangProductInfoAPI,
  patchAdExtensionAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";

const CoupangAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData,
  priceGradeData,
  getUserDetailAPI,
  onSave,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("details");
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryIds, setExpandedHistoryIds] = useState(new Set());
  const [keywordMap, setKeywordMap] = useState({});
  const [refreshLoading, setRefreshLoading] = useState(false);

  useEffect(() => {
    if (activeTab === "history" && isOpen && adData?.ad_id) {
      loadHistoryData();
    }
  }, [activeTab, isOpen, adData?.ad_id]);

  const loadHistoryData = async () => {
    if (!adData?.ad_id) return;
    try {
      setHistoryLoading(true);
      const response = await getMissionHistoryAPI(adData.ad_id);
      const historyList = response.data?.data || [];
      setHistoryData(historyList);

      const keywordIds = new Set();
      historyList.forEach((history) => {
        if (history.beforeSnapshot) {
          if (history.beforeSnapshot.mainKeywordId)
            keywordIds.add(history.beforeSnapshot.mainKeywordId);
          (history.beforeSnapshot.subKeywordIds || []).forEach((id) =>
            keywordIds.add(id)
          );
        }
        if (history.afterSnapshot) {
          if (history.afterSnapshot.mainKeywordId)
            keywordIds.add(history.afterSnapshot.mainKeywordId);
          (history.afterSnapshot.subKeywordIds || []).forEach((id) =>
            keywordIds.add(id)
          );
        }
      });

      const map = {};
      if (keywordData?.length) {
        keywordData.forEach((k) => {
          if (k.keyword_id) map[k.keyword_id] = k.name;
        });
      }
      const missingIds = Array.from(keywordIds).filter((id) => !map[id]);
      if (missingIds.length > 0) {
        try {
          const keywordResponse = await getKeywordNameAPI(
            null,
            null,
            missingIds,
            1,
            2000
          );
          (keywordResponse.data?.data || []).forEach((k) => {
            if (k.keyword_id && !map[k.keyword_id])
              map[k.keyword_id] = k.name;
          });
        } catch (err) {
          console.error("키워드 이름 로드 실패:", err);
        }
      }
      setKeywordMap(map);
    } catch (error) {
      console.error("내역 데이터 로드 실패:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistoryExpanded = (historyId) => {
    setExpandedHistoryIds((prev) => {
      // 시안과 동일: 한 번에 하나만 펼침 (리스트 스크롤로 높이 확보)
      if (prev.has(historyId)) {
        return new Set();
      }
      return new Set([historyId]);
    });
  };

  const handleRefreshAdData = async () => {
    if (!adData?.ad_id || !adData?.productUrl) {
      alert("광고 정보 또는 상품 URL이 없습니다.");
      return;
    }
    const productUrl = (adData.productUrl || "").trim();

    try {
      setRefreshLoading(true);

      const infoRes = await getCoupangProductInfoAPI(productUrl);
      if (!infoRes?.data?.success || !infoRes?.data?.data) {
        alert("쿠팡 상품 정보를 가져오지 못했습니다.");
        setRefreshLoading(false);
        return;
      }
      const data = infoRes.data.data;

      let memoData = {};
      try {
        if (adData.memo) memoData = JSON.parse(adData.memo);
      } catch (e) {}
      const updatedMemo = {
        ...memoData,
        originPrice: data.price ?? memoData.originPrice,
        unitPrice: memoData.unitPrice,
        originProductName: memoData.originProductName,
        adTag: memoData.adTag ?? "",
      };

      const extensionData = {
        ad_id: adData.ad_id,
        storeName: data.storeName ?? adData.storeName,
        missionName: data.productName ?? adData.missionName,
        imageUrl: data.imageUrl ?? adData.imageUrl,
        productUrl: data.originalUrl ?? adData.productUrl,
        memo: JSON.stringify(updatedMemo),
      };
      if (Array.isArray(data.reviews) && data.reviews.length > 0) {
        extensionData.contents = data.reviews;
      }

      await patchAdExtensionAPI(extensionData);
      alert("데이터가 성공적으로 업데이트되었습니다.");
      if (onSave) await onSave();
    } catch (error) {
      console.error("다시 받아오기 실패:", error);
      alert(
        error?.response?.data?.msg ||
          "다시 받아오기 중 오류가 발생했습니다."
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "STATUS_CHANGED":
        return "상태 변경";
      case "UPDATED":
      case "QUANTITY_CHANGED":
      case "PRICE_CHANGED":
      case "DATE_CHANGED":
        return "업데이트";
      case "CREATED":
        return "생성";
      default:
        return action;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  if (!isOpen || !adData) return null;

  const getStatusText = (status) => {
    const map = {
      APPROVED: "진행중",
      PAUSED: "일시정지",
      PENDING: "승인대기중",
      REJECTED: "거절",
      REFUND: "환불",
      DONE: "종료",
    };
    return map[status] || status;
  };

  const getStatusClass = (status) => {
    const map = {
      APPROVED: "status-progress",
      PENDING: "status-pending",
      PAUSED: "status-paused",
      REJECTED: "status-cancelled",
      REFUND: "status-refund",
      DONE: "status-ended",
    };
    return map[status] || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  let memoData = {};
  try {
    if (adData.memo) memoData = JSON.parse(adData.memo);
  } catch (e) {}

  const mainKeywordName =
    keywordData?.find((k) => k.type === "MAIN")?.name || "-";
  const mainKeywordRank = adData.mainKeywordRank ?? "-";

  return (
    <>
      <LoadingOverlay
        isVisible={refreshLoading}
        message="다시 받아오는 중..."
      />
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div
          className="ad-detail-modal ad-detail-shell coupang-ad-detail"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero */}
          <section className="ad-detail-hero" aria-label="광고 요약">
            <img
              className="ad-detail-hero-thumb"
              src={adData.imageUrl || "/logo192.png"}
              alt={adData.missionName || "상품 이미지"}
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
            />
            <div className="ad-detail-hero-main">
              <div className="ad-detail-hero-store">
                {adData.storeName || "-"}
              </div>
              <div
                className="ad-detail-hero-name"
                title={adData.missionName || "-"}
              >
                {adData.missionName || "-"}
              </div>
              <div className="ad-detail-hero-meta">
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                  withParens
                />
                <span
                  className={`ad-detail-chip is-status ${getStatusClass(
                    adData.status,
                  )}`}
                >
                  {getStatusText(adData.status)}
                </span>
              </div>
            </div>
            <div className="ad-detail-hero-actions">
              {user?.role !== "CHECKER" &&
                (adData.status === "PENDING" ||
                  adData.status === "PAUSED" ||
                  adData.status === "APPROVED") && (
                  <button
                    type="button"
                    className="ad-detail-btn ad-detail-btn-refresh"
                    onClick={handleRefreshAdData}
                    disabled={refreshLoading}
                    title="다시 받아오기"
                  >
                    {refreshLoading ? "받아오는 중..." : "다시 받아오기"}
                  </button>
                )}
            </div>
          </section>

          <TabNavigation
            className="ad-detail-segment-tabs"
            tabs={[
              { id: "details", label: "광고 상세 정보" },
              ...(user?.role !== "CHECKER"
                ? [{ id: "history", label: "내역" }]
                : []),
            ]}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />

          <div className="modal-content ad-detail-body">
            {activeTab === "details" && (
              <div className="ad-detail-panel">
                <div className="ad-detail-detail-groups">
                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">광고 정보</h2>
                    <div className="ad-detail-tiles">
                      <div className="ad-detail-tile is-wide">
                        <span className="ad-detail-tile-label">광고명</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={adData.missionName || "-"}
                        >
                          {adData.missionName || "-"}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">카테고리</span>
                        <strong className="ad-detail-tile-value">쿠팡</strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">스토어명</span>
                        <strong className="ad-detail-tile-value">
                          {adData.storeName || "-"}
                        </strong>
                      </div>
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile">
                          <span className="ad-detail-tile-label">미션 방법</span>
                          <strong className="ad-detail-tile-value">
                            {adData.productName || "퀴즈"}
                          </strong>
                        </div>
                      )}
                      <div className="ad-detail-tile is-wide">
                        <span className="ad-detail-tile-label">상품 URL</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={adData.productUrl || "-"}
                        >
                          {adData.productUrl ? (
                            <a
                              href={adData.productUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {adData.productUrl}
                            </a>
                          ) : (
                            "-"
                          )}
                        </strong>
                      </div>
                      {user?.role !== "CHECKER" && (
                        <>
                          <div className="ad-detail-tile">
                            <span className="ad-detail-tile-label">
                              메인 키워드
                            </span>
                            <strong className="ad-detail-tile-value">
                              {mainKeywordName}
                            </strong>
                          </div>
                          <div className="ad-detail-tile">
                            <span className="ad-detail-tile-label">
                              메인 키워드 순위
                            </span>
                            <strong className="ad-detail-tile-value">
                              {mainKeywordRank !== "-"
                                ? `${mainKeywordRank}위`
                                : "-"}
                            </strong>
                          </div>
                          {memoData.originPrice !== undefined && (
                            <div className="ad-detail-tile">
                              <span className="ad-detail-tile-label">원가</span>
                              <strong className="ad-detail-tile-value">
                                {memoData.originPrice}
                              </strong>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </section>

                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">기간</h2>
                    <div className="ad-detail-tiles">
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">광고 시작일</span>
                        <strong className="ad-detail-tile-value">
                          {formatDate(adData.startDate)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">광고 종료일</span>
                        <strong className="ad-detail-tile-value">
                          {formatDate(adData.endDate)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">마감 예정일</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatDate(adData.endDate)}
                        >
                          {formatDate(adData.endDate)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">수량 · 결제</h2>
                    <div className="ad-detail-tiles">
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile is-accent">
                          <span className="ad-detail-tile-label">1회 단가</span>
                          <strong
                            className="ad-detail-tile-value"
                            title={`${formatNumber(
                              Math.round(adData.price / adData.totalQuantity),
                            )}원`}
                          >
                            {formatNumber(
                              Math.round(adData.price / adData.totalQuantity),
                            )}
                            원
                          </strong>
                        </div>
                      )}
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">총 수량</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatNumber(adData.totalQuantity)}
                        >
                          {formatNumber(adData.totalQuantity)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">일일 수량</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatNumber(adData.dailyQuantity)}
                        >
                          {formatNumber(adData.dailyQuantity)}
                        </strong>
                      </div>
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile is-accent">
                          <span className="ad-detail-tile-label">결제 금액</span>
                          <strong
                            className="ad-detail-tile-value"
                            title={`${formatNumber(adData.price || 0)}원`}
                          >
                            {formatNumber(adData.price || 0)}원
                          </strong>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeTab === "history" && (
              <div className="ad-detail-panel">
                <div className="ad-detail-history-head">
                  <h2 className="ad-detail-group-title">변경 내역</h2>
                  {!historyLoading && (
                    <span className="ad-detail-history-count">
                      {historyData.length}건
                    </span>
                  )}
                </div>
                {historyLoading ? (
                  <div className="ad-detail-history-empty">
                    <strong>내역을 불러오는 중...</strong>
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="ad-detail-history-empty">
                    <strong>변경 내역이 없습니다</strong>
                    <p>광고 수정·상태 변경이 발생하면 이곳에 표시됩니다.</p>
                  </div>
                ) : (
                  <div
                    className={`ad-detail-history-list ${
                      historyData.length > 3 ? "is-many" : ""
                    }`}
                  >
                    {historyData.map((history) => {
                      const isExpanded = expandedHistoryIds.has(history._id);
                      const changedAt = history.changedAt
                        ? convertUTCToKoreaTime(history.changedAt)
                        : null;
                      const changedAtStr = changedAt
                        ? changedAt.toLocaleString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";
                      return (
                        <article
                          key={history._id}
                          className={`ad-detail-history-item ${
                            isExpanded ? "is-open" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="ad-detail-history-trigger"
                            onClick={() =>
                              toggleHistoryExpanded(history._id)
                            }
                            aria-expanded={isExpanded}
                          >
                            <span
                              className={`ad-detail-action-badge is-${String(
                                history.action || "",
                              ).toLowerCase()}`}
                            >
                              {getActionLabel(history.action)}
                            </span>
                            <span className="ad-detail-history-reason">
                              {history.reason || "-"}
                            </span>
                            {history.admin_memo ? (
                              <span className="ad-detail-history-memo">
                                ({history.admin_memo})
                              </span>
                            ) : null}
                            <span className="ad-detail-history-time">
                              {changedAtStr}
                            </span>
                            <span className="ad-detail-history-chevron">
                              {isExpanded ? "▾" : "▸"}
                            </span>
                          </button>

                          {isExpanded && (
                            <div className="ad-detail-history-detail">
                              {history.changedFields?.length > 0 && (
                                <div className="ad-detail-changed-fields">
                                  {history.changedFields.map((field) => (
                                    <span
                                      key={field}
                                      className="ad-detail-changed-tag"
                                    >
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="ad-detail-snap-grid">
                                <div className="ad-detail-snap is-before">
                                  <div className="ad-detail-snap-head">
                                    변경 전
                                  </div>
                                  <div className="ad-detail-snap-body">
                                    {history.beforeSnapshot ? (
                                      <HistorySnapshot
                                        snapshot={history.beforeSnapshot}
                                        changedFields={
                                          history.changedFields || []
                                        }
                                        keywordMap={keywordMap}
                                      />
                                    ) : (
                                      <p className="ad-detail-snap-empty">
                                        변경 전 데이터 없음
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="ad-detail-snap is-after">
                                  <div className="ad-detail-snap-head">
                                    변경 후
                                  </div>
                                  <div className="ad-detail-snap-body">
                                    {history.afterSnapshot ? (
                                      <HistorySnapshot
                                        snapshot={history.afterSnapshot}
                                        changedFields={
                                          history.changedFields || []
                                        }
                                        keywordMap={keywordMap}
                                      />
                                    ) : (
                                      <p className="ad-detail-snap-empty">
                                        데이터 없음
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// 내역 스냅샷 (NormalAdDetail과 동일 구조)
const HistorySnapshot = ({ snapshot, changedFields = [], keywordMap = {} }) => {
  const getStatusLabel = (status) => {
    const map = {
      PENDING: "승인대기중",
      APPROVED: "진행중",
      PAUSED: "일시정지",
      REJECTED: "거절",
      DONE: "종료",
      REFUND: "환불",
    };
    return map[status] || status;
  };
  const isChanged = (fieldName) => changedFields.includes(fieldName);
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "예" : "아니오";
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("ko-KR");
    } catch (e) {
      return dateString;
    }
  };
  let memoData = {};
  try {
    if (snapshot.memo) memoData = JSON.parse(snapshot.memo);
  } catch (e) {}
  const renderField = (label, fieldName, value, formatter = null, forceChanged = false) => {
    const formatted = formatter ? formatter(value) : formatValue(value);
    const changed = forceChanged || isChanged(fieldName);
    return (
      <div className="snapshot-row" key={fieldName + label}>
        <span className="snapshot-label">{label}:</span>
        <span className={`snapshot-value ${changed ? "changed-field" : ""}`}>{formatted}</span>
      </div>
    );
  };
  return (
    <div className="snapshot-data">
      {renderField("서비스명", "serviceName", snapshot.serviceName)}
      {renderField("광고명", "missionName", snapshot.missionName)}
      {renderField("상품명", "productName", snapshot.productName)}
      {renderField("스토어명", "storeName", snapshot.storeName)}
      {renderField("상품 URL", "productUrl", snapshot.productUrl)}
      {renderField("상태", "status", getStatusLabel(snapshot.status))}
      {renderField(
        "가격",
        "price",
        snapshot.price ? `${snapshot.price.toLocaleString()}원` : "-"
      )}
      {renderField("총 수량", "totalQuantity", snapshot.totalQuantity)}
      {renderField("일일 수량", "dailyQuantity", snapshot.dailyQuantity)}
      {renderField("시작일", "startDate", formatDate(snapshot.startDate))}
      {renderField("종료일", "endDate", formatDate(snapshot.endDate))}
      {renderField(
        "메인 키워드",
        "mainKeywordId",
        snapshot.mainKeywordId
          ? `${keywordMap[snapshot.mainKeywordId] || "알 수 없음"} (ID: ${snapshot.mainKeywordId})`
          : "-"
      )}
      {renderField(
        "서브 키워드",
        "subKeywordIds",
        snapshot.subKeywordIds?.length
          ? snapshot.subKeywordIds
              .map((id) => `${keywordMap[id] || "알 수 없음"} (ID: ${id})`)
              .join(", ")
          : "-"
      )}
      {memoData.originPrice !== undefined &&
        renderField("원가", "memo", memoData.originPrice, null, isChanged("memo"))}
      {memoData.unitPrice !== undefined &&
        renderField("적용단가", "memo", memoData.unitPrice, null, isChanged("memo"))}
    </div>
  );
};

export default CoupangAdDetail;
